/**
 * features/admin/BulkCommentImport.tsx
 * ------------------------------------------------------------------
 * AUDIT M5 — bulk-import class-teacher comments from CSV. Distinct
 * from BulkImport (which imports students). A class teacher often
 * writes all their comments offline in a spreadsheet; this lets
 * them be loaded in one go instead of typed one student at a time.
 *
 * LKC model: the class-teacher comment is ONE combined field per
 * student (feature #2), stored in
 *   reportComments/{year-term-classId}.entries[studentId]
 *       .classTeacherComment
 * so the CSV is simply: student name, comment. Matched by name
 * within the chosen class (admission number optional tiebreaker).
 * Same structure/conventions as BulkImport.
 * ------------------------------------------------------------------
 */
import { useState, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
// Batch B: undefined-safe write + save modal
import { setDoc } from '@/lib/firebase';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import {
  Upload,
  Download,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { auditFromContext } from '@/lib/audit';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { sanitizeComment } from '@/lib/sanitize';
import { TermKey, termLabel } from '@/lib/school.config';
import { Card, PageHeader, Button } from '@/components/ui/primitives';
import type {
  ReportCommentsDocument,
  StudentReportComments,
} from '@/types';

interface ParsedRow {
  name: string;
  admission: string;
  comment: string;
  studentId?: string;
  error?: string;
}

function norm(s: string) {
  return s.toLowerCase().replace(/\s+/g, ' ').trim();
}

export default function BulkCommentImport({
  year,
  term,
}: {
  year: string;
  term: TermKey;
}) {
  const { classes, students } = useData();
  const { userData } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);

  const [classId, setClassId] = useState('');
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(
    null,
  );

  const roster = students.filter((s) => s.currentClass === classId);

  function template() {
    const csv =
      'StudentName,AdmissionNumber,ClassTeacherComment\n' +
      '"Jane Doe","ADM001","A diligent student who has worked steadily all term."\n' +
      '"John Smith","","Polite and well-conducted; should aim for more consistency."\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lkc_classteacher_comments_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  // minimal CSV line splitter that respects double-quoted fields
  function splitCsvLine(line: string): string[] {
    const out: string[] = [];
    let cur = '';
    let q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (q && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else q = !q;
      } else if (c === ',' && !q) {
        out.push(cur);
        cur = '';
      } else cur += c;
    }
    out.push(cur);
    return out.map((x) => x.trim());
  }

  function parse(text: string) {
    if (!classId) {
      setMsg({ ok: false, text: 'Choose the class first.' });
      return;
    }
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length < 2) {
      setMsg({
        ok: false,
        text: 'CSV needs a header row and at least one comment row.',
      });
      return;
    }
    const header = splitCsvLine(lines[0]).map((h) =>
      h.toLowerCase().replace(/\s/g, ''),
    );
    const iName = header.findIndex((h) => h.includes('name'));
    const iAdm = header.findIndex((h) => h.includes('admission'));
    const iCom = header.findIndex((h) => h.includes('comment'));
    if (iName === -1 || iCom === -1) {
      setMsg({
        ok: false,
        text: 'CSV must have a StudentName and a ClassTeacherComment column.',
      });
      return;
    }

    const parsed: ParsedRow[] = lines.slice(1).map((line) => {
      const cells = splitCsvLine(line);
      const name = cells[iName] ?? '';
      const admission = iAdm > -1 ? (cells[iAdm] ?? '') : '';
      const comment = cells[iCom] ?? '';
      const row: ParsedRow = { name, admission, comment };
      if (!name) {
        row.error = 'Missing student name';
        return row;
      }
      if (!comment) {
        row.error = 'Empty comment';
        return row;
      }
      // match by name within the class; admission breaks ties
      const matches = roster.filter(
        (s) => norm(s.fullName) === norm(name),
      );
      let match = matches[0];
      if (matches.length > 1 && admission) {
        match =
          matches.find(
            (s) => (s.admissionNumber ?? '') === admission,
          ) ?? match;
      }
      if (!match) {
        row.error = matches.length
          ? 'Ambiguous name — add AdmissionNumber'
          : 'No student with this name in the class';
        return row;
      }
      row.studentId = match.id;
      return row;
    });
    setRows(parsed);
    const ok = parsed.filter((r) => !r.error).length;
    setMsg({
      ok: ok > 0,
      text: `${ok} of ${parsed.length} rows matched and ready to import.`,
    });
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => parse(String(r.result));
    r.readAsText(f);
  }

  async function commit() {
    const good = rows.filter((r) => !r.error && r.studentId);
    if (good.length === 0 || !classId) return;
    setImporting(true);
    setSaveModal({ show: true, status: 'saving', message: `Importing ${good.length} comments…` });
    try {
      const cId = `${year}-${term}-${classId}`;
      const ref = doc(db, COLLECTIONS.reportComments, cId);
      const snap = await getDoc(ref);
      const existing =
        (snap.exists()
          ? (snap.data() as ReportCommentsDocument).entries
          : {}) ?? {};
      const entries: Record<string, StudentReportComments> = {
        ...existing,
      };
      const now = new Date().toISOString();
      good.forEach((r) => {
        const prev = entries[r.studentId!] ?? {
          classTeacherComment: '',
          hodComment: '',
        };
        entries[r.studentId!] = {
          ...prev,
          classTeacherComment: sanitizeComment(r.comment, 800),
          classTeacherCommentMeta: {
            authorId: userData?.uid,
            authorRole: userData?.role,
            updatedAt: now,
          },
        };
      });
      // WIPE GUARD (13.7): never write an empty entries map.
      if (Object.keys(entries).length > 0) {
        await setDoc(
          ref,
          {
            year,
            term,
            classId,
            entries,
            updatedAt: now,
          },
          { merge: true },
        );
      }
      auditFromContext(userData, 'comments_bulk_imported', 'academic', {
        targetId: classId, targetType: 'comment',
        details: { year, term, count: good.length },
      });
      setSaveModal({
        show: true,
        status: 'success',
        message: `Imported ${good.length} class-teacher comments. They can be reviewed/edited in Comment Review.`,
      });
      setRows([]);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setImporting(false);
    }
  }

  const okCount = rows.filter((r) => !r.error).length;

  return (
    <div>
      <PageHeader
        title="Bulk Comment Import"
        subtitle={`Class-teacher comments · ${termLabel(term)} ${year}`}
      />

      <Card className="p-5 mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
          Import class-teacher comments from a spreadsheet instead
          of typing them one by one. Columns:{' '}
          <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
            StudentName, AdmissionNumber (optional), ClassTeacherComment
          </code>
          . Comments are matched to students by name within the
          chosen class.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-sm font-medium">Class</label>
            <select
              value={classId}
              onChange={(e) => {
                setClassId(e.target.value);
                setRows([]);
                setMsg(null);
              }}
              className="block mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm sm:w-64"
            >
              <option value="">— select class —</option>
              {[...classes]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </div>
          <Button variant="ghost" onClick={template}>
            <Download size={15} /> Template CSV
          </Button>
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={!classId}
          >
            <Upload size={15} /> Choose CSV
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={onFile}
          />
        </div>
      </Card>

      {msg && (
        <div
          className={`text-sm rounded-xl px-4 py-3 border flex items-center gap-2 mb-4 ${
            msg.ok
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
              : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
          }`}
        >
          {msg.ok ? (
            <CheckCircle2 size={15} />
          ) : (
            <AlertTriangle size={15} />
          )}
          {msg.text}
        </div>
      )}

      {rows.length > 0 && (
        <Card className="overflow-hidden">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="font-bold flex items-center gap-2">
              <FileText size={16} /> Preview ({okCount}/{rows.length}{' '}
              ready)
            </h3>
            <Button
              onClick={commit}
              disabled={importing || okCount === 0}
            >
              {importing ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Upload size={15} />
              )}
              Import {okCount} comment{okCount === 1 ? '' : 's'}
            </Button>
          </div>
          <div className="overflow-x-auto max-h-[420px]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-left">
                <tr>
                  <th className="px-3 py-2">Student (CSV)</th>
                  <th className="px-3 py-2">Comment</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={i}
                    className="border-t border-gray-100 dark:border-gray-700/50"
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.name}
                      {r.admission && (
                        <span className="text-xs text-gray-400">
                          {' '}
                          ({r.admission})
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300 max-w-md truncate">
                      {r.comment}
                    </td>
                    <td className="px-3 py-2">
                      {r.error ? (
                        <span className="text-xs text-red-600 flex items-center gap-1">
                          <AlertTriangle size={12} /> {r.error}
                        </span>
                      ) : (
                        <span className="text-xs text-emerald-600 flex items-center gap-1">
                          <CheckCircle2 size={12} /> matched
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </div>
  );
}
